import { useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  VStack,
  Text,
  useToast,
  useBreakpointValue,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { useAudioContext } from '../context/AudioContext';

interface FeedbackProps {
  onPrivacyClick: () => void;
  onShowInfoOverlay: () => void;
}

const Feedback = ({ onPrivacyClick, onShowInfoOverlay }: FeedbackProps) => {
  const { 
    isOpen: isFeedbackOpen, 
    onOpen: onFeedbackOpen, 
    onClose: onFeedbackClose 
  } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isBlindTestMode } = useAudioContext();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const form = e.currentTarget;
      const formName = form.getAttribute("name") || "abmymix-feedback";
      const data = {
        "form-name": formName,
        name: form.querySelector<HTMLInputElement>('[name="name"]')?.value || "",
        email: form.querySelector<HTMLInputElement>('[name="email"]')?.value || "",
        message: form.querySelector<HTMLTextAreaElement>('[name="message"]')?.value || "",
      };

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data).toString()
      });

      if (response.ok) {
        toast({
          title: "Thank you for your feedback!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onFeedbackClose();
        form.reset();
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      toast({
        title: "Error submitting feedback",
        description: "Please try again later",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBlindTestMode) {
    return null;
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label="Info and feedback"
          icon={<Icon as={FiInfo} boxSize="24px" />}
          variant="ghost"
          size="lg"
          borderRadius="full"
          bg="transparent"
          color="gray.400"
          _hover={{
            color: "accent.400",
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{
            color: "accent.500",
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
        />
        <MenuList 
          bg="gray.900"
          border="1px solid"
          borderColor="gray.800"
          p={0}
          overflow="hidden"
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.3)"
          borderRadius="md"
          minW="180px"
        >
          <MenuItem
            onClick={onShowInfoOverlay}
            bg="gray.900"
            border="none"
            _hover={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _focus={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _active={{ 
              color: "accent.500",
              bg: 'whiteAlpha.100'
            }}
            h="40px"
            fontSize="sm"
            fontWeight="medium"
            transition="color 0.2s, background-color 0.2s"
            borderRadius={0}
            color="gray.300"
            px={4}
          >
            Show Tutorial
          </MenuItem>
          <MenuItem
            onClick={onPrivacyClick}
            bg="gray.900"
            border="none"
            _hover={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _focus={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _active={{ 
              color: "accent.500",
              bg: 'whiteAlpha.100'
            }}
            h="40px"
            fontSize="sm"
            fontWeight="medium"
            transition="color 0.2s, background-color 0.2s"
            borderRadius={0}
            color="gray.300"
            px={4}
          >
            About
          </MenuItem>
          <MenuItem
            onClick={onFeedbackOpen}
            bg="gray.900"
            border="none"
            _hover={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _focus={{ 
              color: "accent.400",
              bg: 'whiteAlpha.50'
            }}
            _active={{ 
              color: "accent.500",
              bg: 'whiteAlpha.100'
            }}
            h="40px"
            fontSize="sm"
            fontWeight="medium"
            transition="color 0.2s, background-color 0.2s"
            borderRadius={0}
            color="gray.300"
            px={4}
          >
            Contact
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Contact/Feedback Modal */}
      <Modal isOpen={isFeedbackOpen} onClose={onFeedbackClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Contact</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text mb={4}>Have a suggestion or found an issue? Let me know!</Text>
            
            <form 
              name="abmymix-feedback" 
              data-netlify="true"
              onSubmit={handleSubmit}
              data-netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="abmymix-feedback" />
              <input type="hidden" name="bot-field" />
              
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    type="text"
                    bg="whiteAlpha.50"
                    borderColor="whiteAlpha.200"
                    _hover={{ borderColor: "whiteAlpha.300" }}
                    _focus={{ borderColor: "accent.500" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    bg="whiteAlpha.50"
                    borderColor="whiteAlpha.200"
                    _hover={{ borderColor: "whiteAlpha.300" }}
                    _focus={{ borderColor: "accent.500" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    name="message"
                    rows={5}
                    bg="whiteAlpha.50"
                    borderColor="whiteAlpha.200"
                    _hover={{ borderColor: "whiteAlpha.300" }}
                    _focus={{ borderColor: "accent.500" }}
                    resize="vertical"
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="accent"
                  width="full"
                  isLoading={isSubmitting}
                >
                  Send Message
                </Button>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Feedback; 